const ELM_NAME = 'load-more';
const PRODUCT_GRID_SELECTOR = '[data-cards-grid]';
const LOAD_MORE_BUTTONS_SELECTOR = '[data-cards-load-more]';
const PAGINATION_SELECTOR = '[data-pagination]';

const isValid = (value) => value !== undefined;

class LoadMore extends HTMLElement {
  static get observedAttributes() {
    return ['data-page-config'];
  }

  /** @type {{ totalPages: Number, currentPage: Number, cardsCount: Number, cardsViewed: Number, sectionToFetch: String }} */
  pageConfig = {
    sectionToFetch: ELM_NAME,
  };

  setPageConfig() {
    const pageConfig = JSON.parse(this.getAttribute('data-page-config') || '{}');
    const { totalPages, currentPage, cardsCount, PageHandle, cardsViewed, sectionToFetch } =
      pageConfig;
    if (
      isValid(totalPages) &&
      isValid(currentPage) &&
      isValid(cardsCount) &&
      isValid(cardsViewed)
    ) {
      this.pageConfig = {
        totalPages: parseInt(totalPages),
        currentPage: parseInt(currentPage),
        cardsCount: parseInt(cardsCount),
        PageHandle,
        cardsViewed: parseInt(cardsViewed),
        sectionToFetch,
      };

      return this.pageConfig;
    }
  }

  setButtonDisabled(button) {
    button.disabled = true;
    button.classList.add('hidden');
    button.parentElement.classList.add('hidden');
  }

  setButtonEnabled(button) {
    button.disabled = false;
    button.classList.remove('hidden');
    button.parentElement.classList.remove('hidden');
  }

  handleLoadMoreClick = (event) => {
    const { currentTarget } = event;
    const loadType = currentTarget.dataset.cardsLoadMore;
    this.isLoading();
    this.handleLoadMoreProducts(loadType);
  };

  handleLoadMoreProducts(loadType, previousPage) {
    const isPopState = previousPage !== undefined;
    const isNext = loadType == 'next';

    // Reset the page index if in popstate
    if (isPopState) {
      this.nextPageIndex = previousPage;
      this.previousPageIndex = previousPage;

      if (isNext) {
        this.nextPageIndex += 1;
        this.previousPageIndex = this.nextPageIndex;
      } else {
        this.previousPageIndex -= 1;
        this.nextPageIndex = this.previousPageIndex;
      }
    } else if (isNext) {
      this.nextPageIndex += 1;
    } else {
      this.previousPageIndex -= 1;
    }

    const pageToFetch = isNext ? this.nextPageIndex : this.previousPageIndex;

    const url = new URL(window.location.href);

    this.previousUrl = url;

    if (!url.searchParams.get('page')) {
      url.searchParams.append('page', `${pageToFetch}`);
    } else {
      url.searchParams.set('page', `${pageToFetch}`);
    }

    const paramsString = url.searchParams.toString();
    const fetchUrl = `${url.pathname}?section_id=${this.pageConfig.sectionToFetch}&${paramsString}`;

    const historyState = {
      page: pageToFetch,
      PageHandle: this.pageConfig.PageHandle,
    };

    this.renderProducts(fetchUrl, url, loadType, pageToFetch, historyState, isPopState);
  }

  async renderProducts(fetchUrl, newUrl, direction, pageToFetch, state, isPopState) {
    try {
      if (pageToFetch < 1) {
        this.isLoading(false);
        this.setButtonDisabled(this.loadMoreButtonPrevious);
        return;
      }

      if (pageToFetch > this.pageConfig.totalPages) {
        this.isLoading(false);
        this.setButtonDisabled(this.loadMoreButtonNext);
        return;
      }

      // Get the data we need
      const response = await fetch(fetchUrl);

      // Check response
      if (response.status != 200) {
        throw response;
      }

      // Get the text data
      const responseText = await response.text();

      // String to DOM
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Get the content from the section
      const newProducts = html.querySelector(PRODUCT_GRID_SELECTOR)?.children;

      if (newProducts && newProducts.length) {
        const cardsViewed = this.pageConfig.cardsViewed + newProducts.length;

        if (isPopState) {
          // Replace the content during popstate
          this.productGrid.replaceChildren(...newProducts);
        } else if (direction == 'next') {
          this.productGrid.append(...newProducts);
        } else {
          this.productGrid.prepend(...newProducts);
        }

        // Update the attribute (triggers attributeChangedCallback lifecycle method)
        this.setAttribute(
          'data-page-config',
          JSON.stringify({
            ...this.pageConfig,
            currentPage: pageToFetch,
            cardsViewed,
          })
        );

        if (pageToFetch == this.pageConfig.totalPages) {
          this.setButtonDisabled(this.loadMoreButtonNext);

          // Re-enable the previous button if we got here from popstate and there are more products to load
          if (this.pageConfig.cardsViewed !== this.pageConfig.cardsCount) {
            this.setButtonEnabled(this.loadMoreButtonPrevious);
          }
        } else if (pageToFetch == 1) {
          this.setButtonDisabled(this.loadMoreButtonPrevious);

          // Re-enable the next button if we got here from popstate and there are more products to load
          if (this.pageConfig.cardsViewed !== this.pageConfig.cardsCount) {
            this.setButtonEnabled(this.loadMoreButtonNext);
          }
        } else if (pageToFetch > 2) {
          this.loadButtons.map(this.setButtonEnabled);
        }
      }

      // Don't push new state if we're in a popstate
      if (!isPopState) {
        history.pushState(state, null, newUrl);
      }

      this.isLoading(false);
    } catch (error) {
      console.error(error);
      this.isLoading(false);
    }
  }

  isLoading(state = true) {
    this.loadButtons.forEach((button) => {
      button.disabled = state;
      state ? button.classList.add('loading') : button.classList.remove('loading');
    });
  }

  handlePopState = (event) => {
    const { state } = event;

    if (state?.PageHandle === this.pageConfig.PageHandle && state?.page !== undefined) {
      const previousPage = parseInt(this.previousUrl.searchParams.get('page'));
      const direction = state.page > previousPage ? 'next' : 'previous';

      this.handleLoadMoreProducts(direction, previousPage);
    }
  };

  connectedCallback() {
    if (this.isConnected) {
      const pageConfig = this.setPageConfig();

      if (pageConfig) {
        this.previousPageIndex = pageConfig.currentPage;
        this.nextPageIndex = pageConfig.currentPage;
        this.productGrid = this.querySelector(PRODUCT_GRID_SELECTOR);
        this.loadButtons = [...this.querySelectorAll(LOAD_MORE_BUTTONS_SELECTOR)];
        this.previousUrl = new URL(window.location.href);

        // Check if we have a previous history object state
        const previousHistoryState = history.state?.constructor === Object ? history.state : {};

        // This ensures the user can click all the way back to initial page load
        history.replaceState(
          {
            ...previousHistoryState,
            page: pageConfig.currentPage,
            PageHandle: pageConfig.PageHandle,
          },
          null,
          window.location.href
        );

        window.addEventListener('popstate', this.handlePopState);

        this.loadButtons.forEach((button) => {
          const direction = button.dataset.cardsLoadMore;

          if (direction == 'previous') {
            this.loadMoreButtonPrevious = button;
          } else if (direction == 'next') {
            this.loadMoreButtonNext = button;
          }

          button.addEventListener('click', this.handleLoadMoreClick);
        });
      }
    }
  }

  attributeChangedCallback() {
    this.setPageConfig();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);

    this.loadButtons.forEach((button) => {
      button.removeEventListener('click', this.handleLoadMoreClick);
    });
  }
}

if (!customElements.get(ELM_NAME)) {
  window.customElements.define(ELM_NAME, LoadMore);
}
